import { CustomerOrderStatus, SubscriptionStatusType } from '../../generated/api/models.js';
import { CustomerOrderBillingInfo } from './CustomerOrderBillingInfo.js';
import { CustomerOrderCustomerInfo } from './CustomerOrderCustomerInfo.js';
import { CustomerOrderInfo } from './CustomerOrderInfo.js';
import { CustomerOrderOrderedProductsList } from './CustomerOrderOrderedProductsList.js';
import { EOECustomerOrderDeviceSubscriptions } from './EOECustomerOrderDeviceSubscriptions';
import { EppDeviceChangeSummaries } from './EppDeviceChange/EppDeviceChangeSummaries.js';
import { EppPendingRedeemDisclaimers } from './EppDeviceChange/EppPendingRedeemDisclaimers.js';
import type { ContactPerson, CustomerOrder, Subscription } from '../../generated/api/models.js';

import './CustomerOrderDetails.scss';

interface EOECustomerOrderContentParams {
  pathTemplate: string;
  customerOrder: CustomerOrder;
  ordererContact?: ContactPerson;
  deviceSubscriptions: Subscription[];
  isInternalCustomer: boolean;
  isApprover: boolean;
}

// userContactId will be same for all deliverOrder when an employee places an order.
const getEmployeeUserContactId = (customerOrder: CustomerOrder): string | undefined =>
  customerOrder?.deliveryOrders.find(deliveryOrder => deliveryOrder.userContactId)?.userContactId;

const activeEppDeviceSubscriptionsOfEmployee = (
  customerOrdersDetail: CustomerOrder,
  deviceSubscriptions?: Subscription[]
): Subscription[] | undefined => {
  const employeeUserContactId = getEmployeeUserContactId(customerOrdersDetail);
  return deviceSubscriptions?.filter(
    item =>
      item.details?.device?.eppSubscription &&
      item.subscriptionContactId === employeeUserContactId &&
      item.subscriptionStatus === SubscriptionStatusType.ACTIVE
  );
};

const findFirstReplacedSubscriptionDisplayId = (customerOrder: CustomerOrder) =>
  customerOrder?.deliveryOrders?.find(deliveryOrder => !!deliveryOrder.replacedSubscriptionDisplayId)
    ?.replacedSubscriptionDisplayId;

export const EOECustomerOrderContent = ({
  pathTemplate,
  customerOrder,
  ordererContact,
  deviceSubscriptions,
  isInternalCustomer,
  isApprover,
}: EOECustomerOrderContentParams) => {
  const activeDeviceSubscriptions = activeEppDeviceSubscriptionsOfEmployee(customerOrder, deviceSubscriptions);
  const orderPendingApproval = customerOrder.status === CustomerOrderStatus.PENDING_APPROVAL;
  const replacedSubscriptionDisplayId = findFirstReplacedSubscriptionDisplayId(customerOrder);

  return (
    <div className="of-customer-order-details__content">
      <CustomerOrderInfo customerOrder={customerOrder} isApprover={isApprover} />
      {isApprover && (
        <EppDeviceChangeSummaries customerOrder={customerOrder} deviceSubscriptions={deviceSubscriptions} />
      )}
      {isApprover && <EppPendingRedeemDisclaimers deviceSubscriptions={deviceSubscriptions} />}
      {orderPendingApproval && activeDeviceSubscriptions && (
        <EOECustomerOrderDeviceSubscriptions
          subscriptions={activeDeviceSubscriptions}
          replacedSubscriptionDisplayId={replacedSubscriptionDisplayId}
        />
      )}
      <CustomerOrderOrderedProductsList
        pathTemplate={pathTemplate}
        customerOrder={customerOrder}
        isApprover={isApprover}
      />
      {isApprover && orderPendingApproval && (
        <>
          <CustomerOrderCustomerInfo ordererContact={ordererContact} />
          <CustomerOrderBillingInfo
            isInternalCustomer={isInternalCustomer}
            selectedBillingAccounts={customerOrder.catalogSelectedBillingAccounts}
          />
        </>
      )}
    </div>
  );
};
